import { type ComponentType, lazy } from 'react';

/** True when this page load was triggered by a reload (including our chunk recovery reload). */
function isNavigationReload(): boolean {
  const nav = performance.getEntriesByType('navigation')[0] as
    | PerformanceNavigationTiming
    | undefined;
  return nav?.type === 'reload';
}

function isChunkLoadError(err: unknown): boolean {
  const msg = String((err as Error)?.message ?? err);
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('Importing a module script failed')
  );
}

let chunkReloadScheduled = false;

/**
 * Like `lazy()`, but after a deploy the browser may still have an old `index.html` that
 * references removed hashed chunks → dynamic import 404. In production, trigger at most one
 * full reload so the shell can be re-fetched (needs correct Cache-Control on the server).
 */
export function lazyWithChunkReload<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(() =>
    factory().catch((err: unknown) => {
      if (!import.meta.env.PROD || !isChunkLoadError(err)) {
        throw err;
      }
      // Avoid infinite reload if the shell is still stale or CDN keeps serving old HTML.
      if (isNavigationReload()) {
        throw err;
      }
      if (chunkReloadScheduled) {
        return new Promise(() => {}) as Promise<{ default: T }>;
      }
      chunkReloadScheduled = true;
      window.location.reload();
      return new Promise(() => {}) as Promise<{ default: T }>;
    })
  );
}
